import { AxiosError } from 'axios';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { notification } from 'ant-design-vue';
import {isLoginPage} from "@/utils/isLoginPage";
import { getRouterList, RouteItem } from '@/config/routerList';

interface NotificationData {
  id: string;
  title?: string;
  message: string;
  type: 'info' | 'warning' | 'confirmation';
  timestamp: Date;
  sourceUrl: string;
  moduleRoute: string;
  moduleName: string;
  userSelection?: string;
}

@Component
export default class Toast extends Vue {
  private notificationParams = {
    description: '',
    duration: 2,
    class: 'highest-stacking-order',
  }

  private readonly NOTIFICATIONS_STORAGE_KEY = 'sp_notifications';
  private readonly MAX_NOTIFICATIONS = 1000;

  private generateNotificationId(): string {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  private getModuleRoute(): string {
    return "/" + window.location.pathname.split('/')[1] || '';
  }

  private getModuleNameByRoute(routePath: string): string {
    // Use imported routerList to find the route
    const routerList = getRouterList();
    const route = this.findRouteByPath(routerList, routePath);
    return route ? route.title : '';
  }

  private findRouteByPath(routerList: RouteItem[], path: string): RouteItem | null {
    for (const route of routerList) {
      if (route.href === path) {
        return route;
      }
      if (route.child && Array.isArray(route.child)) {
        const found = this.findRouteByPath(route.child, path);
        if (found) return found;
      }
    }
    return null;
  }

  private saveNotificationToStorage(notificationData: NotificationData): void {
    try {
      const existingNotifications = this.getNotificationsFromStorage();
      existingNotifications.unshift(notificationData);
      
      // Limit to MAX_NOTIFICATIONS
      if (existingNotifications.length > this.MAX_NOTIFICATIONS) {
        existingNotifications.splice(this.MAX_NOTIFICATIONS);
      }
      
      localStorage.setItem(this.NOTIFICATIONS_STORAGE_KEY, JSON.stringify(existingNotifications));
    } catch (error) {
      console.warn('Failed to save notification to localStorage:', error);
    }
  }

  private getNotificationsFromStorage(): NotificationData[] {
    try {
      const stored = localStorage.getItem(this.NOTIFICATIONS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to retrieve notifications from localStorage:', error);
      return [];
    }
  }

    showAction(
        message: string,
        config: any
    ) {
      if (isLoginPage()) return;

      const [message1, message2] = message.split('.').map(s => s.trim());
      const key = `open${new Date()}`;
      const {action} = config;
      const [successAction] = action;
      
      const moduleRoute = this.getModuleRoute();
      const notificationData: NotificationData = {
        id: this.generateNotificationId(),
        title: message1 ? message1 : '',
        message: message2 ? message2 : '',
        type: 'confirmation',
        timestamp: new Date(),
        sourceUrl: window.location.href,
        moduleRoute: moduleRoute,
        moduleName: this.getModuleNameByRoute(this.getModuleRoute()),
        userSelection: undefined // Will be set when user clicks
      };

      notification.open({
        message: message2 || message1,
        description: message2 ? message1 : '',
        duration: 1000,
        btn: (h: any) => {
          return h(
              'a-button',
              {
                props: {
                  type: 'primary',
                  size: 'default',
                },
                on: {
                  click: () => {
                    notification.close(key);
                    notificationData.userSelection = successAction.text;
                    this.saveNotificationToStorage(notificationData);
                    successAction.onClick();
                  }
                },
              },
              successAction.text,
          );
        },
        key,
      })
    }

  showResponseError(e: AxiosError) {
    if (isLoginPage()) return;

    this.$log.warn(e.stack);
    this.showError(e.response ? e.response.data : e.message);
  }

  showInfo(message: string, duration?: number) {
    if (!message || isLoginPage()) return;

    const moduleRoute = this.getModuleRoute();
    const notificationData: NotificationData = {
      id: this.generateNotificationId(),
      message: message,
      type: 'info',
      timestamp: new Date(),
      sourceUrl: window.location.href,
      moduleRoute: moduleRoute,
      moduleName: this.getModuleNameByRoute(this.getModuleRoute())
    };

    this.saveNotificationToStorage(notificationData);

    notification.success({
      message,
      ...this.notificationParams,
      duration: duration || this.notificationParams.duration,
    });
  }

  showError(message: string, duration?: number) {
    if (!message || isLoginPage()) return;

    const moduleRoute = this.getModuleRoute();
    const notificationData: NotificationData = {
      id: this.generateNotificationId(),
      message: message,
      type: 'warning',
      timestamp: new Date(),
      sourceUrl: window.location.href,
      moduleRoute: moduleRoute,
      moduleName: this.getModuleNameByRoute(this.getModuleRoute())
    };

    this.saveNotificationToStorage(notificationData);

    notification.error({
      message,
      ...this.notificationParams,
      duration: duration || this.notificationParams.duration,
    });
  }

  // Public method to get all stored notifications
  getStoredNotifications(): NotificationData[] {
    return this.getNotificationsFromStorage();
  }

  // Public method to clear all stored notifications
  clearStoredNotifications(): void {
    localStorage.removeItem(this.NOTIFICATIONS_STORAGE_KEY);
  }
}
